import { makeThumbnail } from '../lib/thumbnail';
import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, File as FileIcon, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { api } from '../services/api';
import { UploadResponse, UploadedFile, UploadError } from '../types/api';

interface UploadComponentProps {
  currentPath: string;
  onUploadComplete: (success: boolean) => void;
  className?: string;
  toolbarMode?: boolean;
}

interface UploadProgress {
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
}

const UploadComponent: React.FC<UploadComponentProps> = ({
  currentPath,
  onUploadComplete,
  className = '',
  toolbarMode = false,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [showDialog, setShowDialog] = useState(false);
  const [errors, setErrors] = useState<UploadError[]>([]);
  const [successCount, setSuccessCount] = useState(0);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  const resetUploadState = useCallback(() => {
    setUploadProgress([]);
    setErrors([]);
    setSuccessCount(0);
    setIsUploading(false);
  }, []);

  const handleFiles = useCallback(async (files: FileList) => {
    if (files.length === 0) return;

    setIsUploading(true);
    resetUploadState();

    // Create progress tracking for each file
    const initialProgress = Array.from(files).map(file => ({
      file,
      progress: 0,
      status: 'pending' as const
    }));
    setUploadProgress(initialProgress);

    try {
      // Simulate progress for better UX (since we can't track actual progress easily)
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => prev.map(item => {
          if (item.status === 'pending' || (item.status === 'uploading' && item.progress < 90)) {
            const newProgress = Math.min(item.status === 'pending' ? 10 : item.progress + 10, 90);
            return { ...item, progress: newProgress, status: 'uploading' as const };
          }
          return item;
        }));
      }, 200);

      // Generate thumbnails client-side first (canvas for images, seek+canvas
      // for videos) so they upload alongside the originals in one request
      const thumbs = new Map<string, Blob>();
      await Promise.all(
        Array.from(files).map(async (file) => {
          const thumb = await makeThumbnail(file);
          if (thumb) thumbs.set(file.name, thumb);
        }),
      );

      const result: UploadResponse = await api.uploadFiles(files, currentPath, thumbs);

      clearInterval(progressInterval);

      // Mark all files as completed
      setUploadProgress(prev => prev.map(item => {
        const uploadedFile = result.uploaded.find((u: UploadedFile) => u.filename === item.file.name);
        const error = result.errors.find((e: UploadError) => e.filename === item.file.name);

        return {
          ...item,
          progress: 100,
          status: uploadedFile ? 'success' : (error ? 'error' : 'success'),
          error: error?.error
        };
      }));

      setErrors(result.errors);
      setSuccessCount(result.successfulUploads);

      // Complete after a short delay to show success states
      setTimeout(() => {
        onUploadComplete(result.successfulUploads > 0);
        setShowDialog(false);
        resetUploadState();
      }, 2000);

    } catch (error) {
      console.error('Upload failed:', error);
      setUploadProgress(prev => prev.map(item => ({
        ...item,
        progress: 100,
        status: 'error',
        error: error instanceof Error ? error.message : 'Upload failed'
      })));

      setTimeout(() => {
        setIsUploading(false);
      }, 2000);
    }
  }, [currentPath, onUploadComplete, resetUploadState]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFiles(files);
    }
  }, [handleFiles]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFiles(files);
    }
  }, [handleFiles]);

  const openFileDialog = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (filename: string) => {
    const ext = filename.toLowerCase().split('.').pop();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext || '')) {
      return <FileIcon className="w-6 h-6 text-blue-500" />;
    } else if (['mp4', 'mov', 'avi', 'mkv', 'webm', 'm4v'].includes(ext || '')) {
      return <FileIcon className="w-6 h-6 text-purple-500" />;
    } else {
      return <FileIcon className="w-6 h-6 text-gray-500" />;
    }
  };

  if (!showDialog) {
    if (toolbarMode) {
      return (
        <button
          onClick={() => setShowDialog(true)}
          className="flex flex-col items-center gap-0.5 px-5 py-2 text-blue-600 rounded-xl hover:bg-blue-50 transition-colors touch-manipulation"
        >
          <Upload className="h-5 w-5" />
          <span className="text-[10px] font-medium">Upload</span>
        </button>
      );
    }
    return (
      <div className={className}>
        <button
          onClick={() => setShowDialog(true)}
          className="flex items-center justify-center gap-2 px-4 py-2 min-h-[40px] bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors touch-manipulation text-sm font-medium"
        >
          <Upload className="w-4 h-4" />
          <span>Upload</span>
        </button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 safe-area-bottom safe-area-top">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[85vh] modal-split-view overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b">
          <h2 className="text-lg sm:text-xl font-semibold">Upload Files</h2>
          <button
            onClick={() => {
              if (!isUploading) {
                setShowDialog(false);
                resetUploadState();
              }
            }}
            className="p-3 min-h-[44px] min-w-[44px] hover:bg-gray-100 rounded-lg transition-colors touch-manipulation"
            disabled={isUploading}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Upload Area */}
        <div className="p-6">
          <div className="mb-4">
            <p className="text-sm text-gray-600">
              Uploading to: <span className="font-mono text-gray-800">{currentPath || 'Root'}</span>
            </p>
          </div>

          {!isUploading && uploadProgress.length === 0 && (
            <div
              ref={dropZoneRef}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={openFileDialog}
              className={`
                border-2 border-dashed rounded-lg p-4 sm:p-6 md:p-8 text-center cursor-pointer transition-colors touch-manipulation
                ${isDragOver
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400'
                }
              `}
            >
              <Upload className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4 text-gray-400" />
              <h3 className="text-base sm:text-lg font-medium mb-2">
                Drag & Drop Files Here
              </h3>
              <p className="text-sm sm:text-base text-gray-600 mb-4">
                or click to browse your device
              </p>
              <button className="px-4 py-3 min-h-[44px] sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors touch-manipulation">
                Choose Files
              </button>
              <p className="text-xs text-gray-500 mt-4 px-2">
                Supported: JPG, PNG, GIF, MP4, MOV, PDF (Max 100MB per file)
              </p>
            </div>
          )}

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,video/*,.pdf"
            onChange={handleFileSelect}
            className="hidden"
          />

          {/* Upload Progress */}
          {uploadProgress.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-base sm:text-lg font-medium">Uploading Files</h3>
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {uploadProgress.map((item, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    {getFileIcon(item.file.name)}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.file.name}</p>
                      <p className="text-xs text-gray-500">
                        {formatFileSize(item.file.size)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {item.status === 'uploading' && (
                        <>
                          <div className="w-16 sm:w-20 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${item.progress}%` }}
                            />
                          </div>
                          <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                        </>
                      )}
                      {item.status === 'success' && (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      )}
                      {item.status === 'error' && (
                        <AlertCircle className="w-5 h-5 text-red-500" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Summary */}
          {successCount > 0 && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 text-green-800">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">
                  {successCount} file{successCount !== 1 ? 's' : ''} uploaded successfully!
                </span>
              </div>
            </div>
          )}

          {errors.length > 0 && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <h4 className="font-medium text-red-800 mb-2">Upload Errors</h4>
              <div className="space-y-2">
                {errors.map((error, index) => (
                  <div key={index} className="flex items-center gap-2 text-red-700">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm">
                      {error.filename}: {error.error}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row sm:justify-end gap-3 p-4 sm:p-6 border-t bg-gray-50">
          <button
            onClick={() => {
              setShowDialog(false);
              resetUploadState();
            }}
            className="px-4 py-3 min-h-[44px] text-gray-700 hover:text-gray-900 transition-colors touch-manipulation"
            disabled={isUploading}
          >
            {uploadProgress.length > 0 ? 'Close' : 'Cancel'}
          </button>
          {!isUploading && uploadProgress.length === 0 && (
            <button
              onClick={openFileDialog}
              className="px-4 py-3 min-h-[44px] bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors touch-manipulation"
            >
              Choose Files
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadComponent;